<script setup lang="ts">
/**
 * "มีระบบเวอร์ชันใหม่ · โหลดหน้าใหม่" (owner 28 ก.ย. 69 — a tab opened before a deploy runs the old app: the menu picture
 * showed only "ตรวจสลิป"). lib/staleBuild.ts finds the newer build (every 5 min + on focus); this bar only asks.
 * Never reloads by itself; if something typed would be lost it says what, and the staff choose. "ภายหลัง" hides it
 * for 30 minutes.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { newBuild, setReloadGuard, unsavedText } from '../lib/staleBuild'
import { useMenuRead } from '../stores/menuRead'
import { useOrders } from '../stores/order'
import Icon from './ui/Icon.vue'

const SNOOZE_MS = 30 * 60 * 1000

const orders = useOrders()
const mr = useMenuRead()

const snoozed = ref(false)
const asking = ref(false)
let timer: number | undefined

const pending = computed(() =>
  unsavedText({
    // unsent replies are kept across a reload now (lib/replyDrafts.ts) — only what has no copy is at risk
    texts: 0,
    orders: Object.values(orders.forms).filter((f) => f.items.length > 0).length,
    reading: !!mr.src && mr.rows.length > 0,
  }),
)
const show = computed(() => !!newBuild.value && !snoozed.value)

function reload(force = false): void {
  if (pending.value && !force) {
    asking.value = true
    return
  }
  window.location.reload()
}
function later(): void {
  asking.value = false
  snoozed.value = true
  window.clearTimeout(timer)
  timer = window.setTimeout(() => (snoozed.value = false), SNOOZE_MS)
}

onMounted(() => setReloadGuard(() => !pending.value))
onBeforeUnmount(() => {
  window.clearTimeout(timer)
  setReloadGuard(() => true)
})
</script>

<template>
  <!-- bottom-left over the chat list column (never over the typing box and its send button) -->
  <div v-if="show" class="nvb" role="status" aria-live="polite">
    <span class="nvb-ic"><Icon name="refresh" /></span>
    <span v-if="!asking" class="nvb-t">มีระบบเวอร์ชันใหม่</span>
    <span v-else class="nvb-t warn">{{ pending }} จะหายถ้าโหลดตอนนี้</span>
    <span class="nvb-b">
      <button type="button" class="nvb-go" @click="reload(asking)">{{ asking ? 'โหลดเลย' : 'โหลดหน้าใหม่' }}</button>
      <button v-if="!asking" type="button" class="nvb-lt" @click="later">ภายหลัง</button>
      <button v-else type="button" class="nvb-lt" @click="asking = false">ยังก่อน</button>
    </span>
  </div>
</template>

<style scoped>
.nvb {
  position: fixed;
  left: 76px; /* rail 64 px + 12 */
  bottom: 16px;
  z-index: 60;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 6px 10px;
  width: 296px; /* inside the 320 px chat list */
  padding: 10px 10px 10px 12px;
  border: 1px solid #bfdccd;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(12, 18, 15, 0.14);
}
.nvb-ic {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--acs);
  color: var(--ac2);
}
.nvb-ic :deep(.ic) {
  width: 15px;
  height: 15px;
}
.nvb-t {
  min-width: 0;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 21px;
  color: var(--ink);
}
.nvb-t.warn {
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  color: var(--warn);
}
.nvb-b {
  grid-column: 2;
  display: flex;
  gap: 6px;
}
.nvb-go,
.nvb-lt {
  flex: none;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
.nvb-go {
  background: var(--ac);
  color: #fff;
}
.nvb-go:hover {
  background: var(--ac2);
}
.nvb-lt {
  color: var(--mut);
  font-weight: 500;
}
.nvb-lt:hover {
  background: #f3f6f4;
  color: var(--ink);
}
@media (max-width: 760px) {
  .nvb {
    left: 12px;
    right: 12px;
    bottom: 12px;
    width: auto;
  }
}
</style>
